import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { normaliseName } from '../lib/textCase';
import { useLaunchStatus } from '../lib/launchStatus';
import { earlyAccessApi } from '../lib/api';
import { EarlyAccessForm } from '../components/EarlyAccessForm';
import { stashInvitationSecret } from '../lib/invitationSecret';

// Full-page loading overlay for key generation
const KeyGenerationOverlay = () => {
  return (
    <div className="fixed inset-0 bg-ground/95 z-50 flex items-center justify-center px-4">
      <div className="bg-surface rounded-xl p-7 sm:p-9 w-full max-w-md">
        <Loader2 className="w-6 h-6 animate-spin text-brand" aria-hidden="true" />
        <h2 className="font-heading text-[24px] font-semibold tracking-[-0.02em] mt-5">
          Making your encryption keys
        </h2>
        <p className="text-ink-70 mt-3">
          Your browser is creating the keys that lock your records. Only your password
          can open them. This takes a few seconds — keep this page open.
        </p>
        <ol className="mt-6 space-y-2 text-[15px] text-ink-70" aria-live="polite">
          <li className="text-ink">Creating your account</li>
          <li className="text-ink">Generating your RSA-4096 key pair</li>
          <li className="text-ink-55">Sealing your private key with your password</li>
        </ol>
      </div>
    </div>
  );
};

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { comingSoon, loading: statusLoading } = useLaunchStatus();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const inviteToken = searchParams.get('invite');
  // A shared-access invitation being accepted (distinct from the coming-soon
  // waitlist token above): `?invitation=<id>#s=<secret>` — the secret only
  // ever travels in the fragment, never sent to the server on this GET.
  const sharedInvitationId = searchParams.get('invitation');
  const sharedInvitationSecret = location.hash.startsWith('#s=')
    ? decodeURIComponent(location.hash.slice(3))
    : null;
  // 'checking' | 'valid' | 'invalid' | null (no token to check / not needed)
  const [inviteState, setInviteState] = useState(inviteToken ? 'checking' : null);
  const [inviteError, setInviteError] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingKeys, setGeneratingKeys] = useState(false);

  // While coming-soon mode is on, a registration needs a valid invite link
  // (?invite=<token>). Resolve it once so we know whose email to lock in.
  useEffect(() => {
    if (!inviteToken) return;
    let live = true;
    (async () => {
      try {
        const { data } = await earlyAccessApi.inviteLookup(inviteToken);
        if (!live) return;
        setEmail(data.email);
        setInviteState('valid');
      } catch (err) {
        if (!live) return;
        setInviteError(err?.response?.data?.detail || 'This invite link is not valid.');
        setInviteState('invalid');
      }
    })();
    return () => { live = false; };
  }, [inviteToken]);

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One capital letter' },
    { met: /[a-z]/.test(password), text: 'One lower-case letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const allRequirementsMet = passwordRequirements.every(req => req.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      toast.error('Your password needs a few more things — see the list under the field');
      return;
    }

    if (!passwordsMatch) {
      toast.error('The two passwords do not match');
      return;
    }

    setLoading(true);
    setGeneratingKeys(true);

    try {
      const cleanedName = normaliseName(fullName) || fullName;
      await register(email, password, cleanedName, inviteToken);
      // The recovery code dialog is shown by the app root (see AuthContext);
      // it stays up across the redirect to MFA setup. Everyone goes through
      // MFA setup and the recovery code the same way, invitee or not — this
      // is a real account guarding real access either way.
      toast.success('Account created. First, save your recovery code.');
      if (sharedInvitationId) {
        // Stash the accept secret for the account picker to pick up once
        // MFA setup / onboarding are done (Onboarding.jsx skips its own
        // nudge and lands there for a pure invitee — see has_own_data).
        stashInvitationSecret(sharedInvitationId, sharedInvitationSecret);
      }
      navigate('/setup-mfa');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'We could not create the account');
    } finally {
      setLoading(false);
      setGeneratingKeys(false);
    }
  };

  const BrandLink = () => (
    <div className="mb-6">
      <Link to="/" className="inline-flex items-center gap-3 rounded-md" aria-label="Precious Data home">
        <PdMark size={28} className="text-brand" />
        <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
      </Link>
    </div>
  );

  const stillChecking = statusLoading || (inviteToken && inviteState === 'checking');
  // A shared-access invitation (see routers/auth.py's register()) bypasses
  // the waiting list gate entirely — someone invited to help with someone
  // else's records shouldn't need a waitlist invite of their own.
  const registrationsClosed = comingSoon && !sharedInvitationId && (!inviteToken || inviteState === 'invalid');

  if (stillChecking) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <BrandLink />
          <div className="bg-surface rounded-xl p-7 sm:p-9" data-testid="register-status-loading">
            <Loader2 className="w-5 h-5 animate-spin text-brand" aria-hidden="true" />
          </div>
        </div>
      </div>
    );
  }

  if (registrationsClosed) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <BrandLink />
          <div className="bg-surface rounded-xl p-7 sm:p-9" data-testid="register-closed">
            <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">Not open yet</h1>
            <p className="text-ink-70 mt-3">
              {inviteToken
                ? (inviteError || 'This invite link is not valid.')
                : "We're finishing the last few things before we open this up. Join the waiting list and we'll email you when it's your turn."}
            </p>
            <EarlyAccessForm className="mt-7" testIdPrefix="register-early-access" />
            <p className="text-[15px] text-ink-70 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-brand font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
      {/* Full-page key generation overlay */}
      {generatingKeys && <KeyGenerationOverlay />}

      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-3 rounded-md" aria-label="Precious Data home">
            <PdMark size={28} className="text-brand" />
            <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
          </Link>
        </div>

        <div className="bg-surface rounded-xl p-7 sm:p-9">
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">Create your account</h1>
          <p className="text-ink-70 mt-3">
            {sharedInvitationId
              ? "You're creating an account to accept an invitation to someone else's records. Use the email address the invite was sent to."
              : 'Everything you add is encrypted so only you can read it.'}
          </p>

          <form onSubmit={handleRegister} className="space-y-5 mt-7">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-[15px] font-semibold block">What&apos;s your name?</Label>
              <p className="text-sm text-ink-55">The name you use now. You can add earlier names later.</p>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={(e) => setFullName(normaliseName(e.target.value) || e.target.value)}
                autoComplete="name"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[15px] font-semibold block">What&apos;s your email address?</Label>
              {inviteState === 'valid' && (
                <p className="text-sm text-ink-55">Your invite is for this address.</p>
              )}
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="username"
                required
                disabled={inviteState === 'valid'}
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[15px] font-semibold block">Choose a password</Label>
              <p className="text-sm text-ink-55">
                This password is also the key to your records. We cannot reset it for you, so you
                will get a recovery code next.
              </p>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="register-password-input"
              />

              {/* Password requirements */}
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm" aria-label="Password requirements">
                {passwordRequirements.map((req) => (
                  <li
                    key={req.text}
                    className={`flex items-center gap-2 ${req.met ? 'text-brand-ink' : 'text-ink-55'}`}
                  >
                    <span
                      className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${req.met ? 'bg-brand' : 'bg-shape'}`}
                      aria-hidden="true"
                    />
                    {req.text}
                    <span className="sr-only">{req.met ? ' (done)' : ' (not yet)'}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[15px] font-semibold block">Type it once more</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                aria-invalid={confirmPassword && !passwordsMatch ? true : undefined}
                data-testid="register-confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-sm text-breach-ink">The two passwords do not match yet.</p>
              )}
            </div>

            <p className="text-sm text-ink-55" data-testid="register-stats-disclosure">
              When your documents are analysed, we'll add anonymised counts (redactions,
              recoveries — never your text or anything that identifies you) to build a
              picture of how organisations handle care leavers' data. On by default —
              you can turn it off anytime in Profile → Privacy.
            </p>

            <Button
              type="submit"
              disabled={loading || !allRequirementsMet || !passwordsMatch}
              className="w-full"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          <p className="text-[15px] text-ink-70 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-brand font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
